import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CitationTooltip from './CitationTooltip';
import { useTheme } from '../contexts/ThemeContext';

interface CitationData {
  document_name: string;
  document_display_name?: string; // For display purposes
  page: number;
  paragraph?: number;
  text: string;
  document_type?: string;
  company_name?: string;
  document_period?: string;
  document_id?: string; // ID único del documento (UUID)
  project_id?: string; // ID del proyecto al que pertenece el documento
  chunk_id?: string; // ID del chunk para buscar tablas asociadas
}

interface CitationBadgeProps {
  citationNumber: string;
  citationData: CitationData;
  onOpenDocument: () => void;
  onOpenChunk?: () => void;
}

const CitationBadge: React.FC<CitationBadgeProps> = ({
  citationNumber,
  citationData,
  onOpenDocument,
  onOpenChunk,
}) => {
  const { theme } = useTheme();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
  const badgeRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate tooltip position based on badge position in viewport
  const updateTooltipPosition = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      setTooltipPosition(spaceBelow > spaceAbove || spaceAbove < 300 ? 'bottom' : 'top');
    }
  };

  // Clear any pending timeouts
  const clearAllTimeouts = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Handle mouse enter on badge (hover behavior)
  const handleMouseEnter = () => {
    // Clear any pending close timeout
    clearAllTimeouts();

    // Show tooltip after a small delay (smoother UX)
    hoverTimeoutRef.current = setTimeout(() => {
      updateTooltipPosition();
      setIsTooltipVisible(true);
    }, 100);
  };

  // Handle mouse leave from badge
  const handleMouseLeave = () => {
    clearAllTimeouts();

    // Close tooltip after a delay
    closeTimeoutRef.current = setTimeout(() => {
      setIsTooltipVisible(false);
    }, 200);
  };

  // Handle mouse enter on tooltip (keep it open)
  const handleTooltipMouseEnter = () => {
    clearAllTimeouts();
  };

  // Handle mouse leave from tooltip
  const handleTooltipMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsTooltipVisible(false);
    }, 200);
  };

  // Handle click on badge (open document directly)
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearAllTimeouts();

    // Close tooltip and open document
    setIsTooltipVisible(false);
    onOpenDocument();
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  return (
    <span className="relative inline-block group">
      <sup
        ref={badgeRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`text-[10px] ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} px-0.5 rounded cursor-pointer transition-all duration-200 select-none`}
        role="button"
        tabIndex={0}
        aria-label={`Citation ${citationNumber}: ${citationData.document_name}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as any);
          }
        }}
      >
        [{citationNumber}]
      </sup>

      {isTooltipVisible &&
        badgeRef.current &&
        createPortal(
          <div
            ref={tooltipRef}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
            className={`fixed z-50 ${tooltipPosition === 'top' ? 'bottom-auto' : 'top-auto'}`}
            style={{
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: 'calc(100vw - 2rem)',
              ...(tooltipPosition === 'top'
                ? {
                    bottom: `calc(100vh - ${badgeRef.current.getBoundingClientRect().top}px + 8px)`,
                  }
                : { top: `${badgeRef.current.getBoundingClientRect().bottom}px` }),
            }}
          >
            <CitationTooltip
              citationData={citationData}
              position={tooltipPosition}
              onOpenPDF={onOpenDocument}
              onOpenChunk={() => {
                setIsTooltipVisible(false);
                onOpenChunk?.();
              }}
            />
          </div>,
          document.body
        )}
    </span>
  );
};

export default CitationBadge;
